import { useTranslation } from "react-i18next";
import { FaPencil, FaTrashCan } from "react-icons/fa6";
import { FiCheck } from "react-icons/fi";

const RICH_CONTENT_CLASS =
  "min-w-0 max-w-full overflow-x-auto break-words [overflow-wrap:anywhere] [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words";

const getPartContent = (parts, matcher) =>
  parts?.find((part) => matcher(part.contentType || ""))?.contentBody;

const renderHintContent = (part, idx) => {
  if ((part.contentType || "").startsWith("text")) {
    return (
      <div
        key={idx}
        className={`text-gray-600 ${RICH_CONTENT_CLASS}`}
        dangerouslySetInnerHTML={{ __html: part.contentBody }}
      />
    );
  }
  if ((part.contentType || "").startsWith("image")) {
    return (
      <img
        key={idx}
        src={part.contentBody}
        alt="Hint"
        className="max-w-full h-auto object-cover rounded-lg border text-sm my-2"
      />
    );
  }
  return null;
};

/**
 * Shared question/card tile used on Subject detail, Workshop, Quiz, etc.
 *
 * Optional creator actions: pass `onEdit` / `onDelete`.
 * Optional selection: pass `selectMode` + `selected` + `onSelect`.
 * Optional detail footer: pass `onDetail`.
 */
export default function QuestionCard({
  item,
  index = 0,
  complexity,
  onEdit,
  onDelete,
  onDetail,
  selectMode = false,
  selected = false,
  onSelect,
  showAnswer = true,
  showHints = true,
  showAiBadges = true,
  showHeader = true,
  layout = "card",
  embedded = false,
  className = "",
}) {
  const { t } = useTranslation();

  const questionImage = getPartContent(
    item?.question?.parts,
    (type) => type.includes("image") || type.includes("unknown")
  );
  const question = getPartContent(item?.question?.parts, (type) =>
    type.includes("text")
  );
  const answer = getPartContent(item?.answers?.[0]?.parts, (type) =>
    type.includes("text")
  );

  const showActions = Boolean(onEdit || onDelete) && !selectMode;
  const titlePadClass = showActions || selectMode ? "pr-16" : "";

  if (layout === "list") {
    return (
      <div
        className={`group relative flex min-w-0 items-center gap-3 overflow-hidden ${
          embedded
            ? "bg-transparent"
            : "rounded-2xl border bg-white px-3 py-2.5 transition-shadow duration-200"
        } ${selectMode ? "cursor-pointer" : ""} ${
          !embedded && selected
            ? "border-blue-400 shadow-md ring-2 ring-blue-500/25"
            : !embedded
              ? "border-gray-100 hover:shadow-md"
              : ""
        } ${className}`}
        onClick={selectMode ? () => onSelect?.(item?.cardId) : undefined}
      >
        {questionImage ? (
          <img
            src={questionImage}
            alt=""
            draggable={false}
            className="h-12 w-16 shrink-0 rounded-lg object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          {showHeader ? (
            <p className="truncate text-[12px] text-lightgray">
              {{
                INPUT: t("answerTypeInput"),
                MEMORY: t("answerTypeMemory"),
                TEST: t("answerTypeTest"),
              }[String(item?.answerType || "").toUpperCase()] ||
                item?.answerType ||
                "N/A"}
              {" · "}
              {t("complexity")} {complexity !== undefined ? complexity : "N/A"}
            </p>
          ) : null}
          <div
            className={`line-clamp-2 text-sm leading-snug text-gray-800 ${RICH_CONTENT_CLASS}`}
            dangerouslySetInnerHTML={{ __html: question || "N/A" }}
          />
          {showAnswer ? (
            <div
              className={`mt-0.5 line-clamp-1 text-xs text-gray-500 ${RICH_CONTENT_CLASS}`}
              dangerouslySetInnerHTML={{ __html: answer || "" }}
            />
          ) : null}
        </div>
        {(showActions || selectMode || onDetail) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {selectMode ? (
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  selected
                    ? "border-blue-500 bg-blue-500"
                    : "border-gray-300 bg-white"
                }`}
              >
                {selected && <FiCheck className="text-sm text-white" />}
              </div>
            ) : (
              <>
                {onEdit ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(item);
                    }}
                    className="rounded-2xl border border-gray-100 bg-white p-2 text-blue-600 transition-colors hover:bg-blue-50"
                    aria-label="Edit"
                  >
                    <FaPencil className="size-3.5" />
                  </button>
                ) : null}
                {onDelete ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                    className="rounded-2xl border border-gray-100 bg-white p-2 text-red-600 transition-colors hover:bg-red-50"
                    aria-label="Delete"
                  >
                    <FaTrashCan className="size-3.5" />
                  </button>
                ) : null}
                {onDetail ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDetail(item);
                    }}
                    className="rounded-2xl px-2 py-1 text-sm font-semibold text-darkblue hover:underline"
                  >
                    {t("detail")}
                  </button>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  const hasAiAudio = Boolean(
    item?.questionTextAI ||
      item?.answerTextAI ||
      item?.hintTextAI ||
      item?.questionAudio ||
      item?.answerAudio ||
      item?.hintAudio ||
      item?.strictCheck
  );

  return (
    <div
      className={`group relative flex min-w-0 flex-col overflow-hidden rounded-2xl border bg-white p-4 transition-shadow duration-200 ${
        selectMode ? "cursor-pointer" : ""
      } ${
        selected
          ? "border-blue-400 shadow-md ring-2 ring-blue-500/25"
          : "border-gray-100 hover:shadow-md"
      } ${className}`}
      onClick={selectMode ? () => onSelect?.(item?.cardId) : undefined}
    >
      {(showActions || selectMode) && (
        <div className="absolute right-4 top-4 z-10 flex items-center gap-1.5">
          {selectMode ? (
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                selected
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300 bg-white"
              }`}
            >
              {selected && <FiCheck className="text-sm text-white" />}
            </div>
          ) : (
            <>
              {onEdit ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}
                  className="rounded-2xl border border-gray-100 bg-white p-2 text-blue-600 transition-colors hover:bg-blue-50"
                  aria-label="Edit"
                >
                  <FaPencil className="size-3.5" />
                </button>
              ) : null}
              {onDelete ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }}
                  className="rounded-2xl border border-gray-100 bg-white p-2 text-red-600 transition-colors hover:bg-red-50"
                  aria-label="Delete"
                >
                  <FaTrashCan className="size-3.5" />
                </button>
              ) : null}
            </>
          )}
        </div>
      )}

      {showHeader ? (
        <div className={`mb-3.5 min-w-0 ${titlePadClass}`}>
          <p className="truncate text-[13px] text-lightgray">
            <span>
              {{
                INPUT: t("answerTypeInput"),
                MEMORY: t("answerTypeMemory"),
                TEST: t("answerTypeTest"),
              }[String(item?.answerType || "").toUpperCase()] ||
                item?.answerType ||
                "N/A"}
            </span>
            {" · "}
            {t("complexity")} {complexity !== undefined ? complexity : "N/A"}
          </p>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-semibold text-gray-500">
            {t("question")}
          </p>
          <div
            className={`text-sm leading-relaxed text-gray-800 ${RICH_CONTENT_CLASS}`}
            dangerouslySetInnerHTML={{ __html: question || "N/A" }}
          />
        </div>

        {questionImage ? (
          <div className="overflow-hidden rounded-2xl bg-[#FFF5E6]">
            <img
              src={questionImage}
              alt={"questionImage" + (index + 1)}
              loading="lazy"
              className="max-h-36 w-full object-cover"
            />
          </div>
        ) : null}

        {showAnswer ? (
          <div className="min-w-0">
            <p className="mb-1 text-xs font-semibold text-gray-500">
              {t("answer")}
            </p>
            <div
              className={`text-sm leading-relaxed text-gray-800 ${RICH_CONTENT_CLASS}`}
              dangerouslySetInnerHTML={{ __html: answer || "N/A" }}
            />
          </div>
        ) : null}

        {showHints && item?.hints?.length > 0 ? (
          <div className="min-w-0">
            <p className="mb-1 text-xs font-semibold text-gray-500">
              {t("Prompter")}
            </p>
            <div className="min-w-0 space-y-1 text-sm text-gray-600">
              {item.hints.flatMap((hint) =>
                (hint.parts || []).map(renderHintContent)
              )}
            </div>
          </div>
        ) : null}

        {showAiBadges && hasAiAudio ? (
          <div className="flex flex-wrap gap-1.5">
            {(item?.questionTextAI ||
              item?.answerTextAI ||
              item?.hintTextAI ||
              item?.questionAudio ||
              item?.answerAudio ||
              item?.hintAudio) && (
              <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-medium text-violet-700">
                {t("aiAudioSection") || "AI / Audio"}
              </span>
            )}
            {item?.strictCheck ? (
              <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                {t("strictCheck") || "Strict check"}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {!selectMode && onDetail ? (
        <button
          type="button"
          className="mt-3.5 w-full border-t border-gray-100 pt-2.5 text-center text-sm font-semibold text-darkblue transition-colors hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            onDetail(item);
          }}
        >
          {t("detail")}
        </button>
      ) : null}
    </div>
  );
}
